import { useMemo } from 'react';

interface SortConfig {
  key: string | null;
  dir: 'asc' | 'desc';
}

interface UseFilteredSortedDataProps<T> {
  data: T[];
  filter: string;
  excludeFilter: boolean;
  sort: SortConfig;
  filterKeys: (keyof T)[];
}

export const useFilteredSortedData = <T extends object>({
  data,
  filter,
  excludeFilter,
  sort,
  filterKeys
}: UseFilteredSortedDataProps<T>): T[] => {
  return useMemo(() => {
    const filtered = data.filter(item => {
      if (filter !== '') {
        const matches = filterKeys.some(key => {
          const value = item[key];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(filter.toLowerCase());
          }
          return false;
        });
        return excludeFilter ? !matches : matches;
      }
      return true;
    });

    if (sort.key) {
      filtered.sort((a, b) => {
        const aVal = a[sort.key as keyof T];
        const bVal = b[sort.key as keyof T];
        
        if (aVal < bVal) return sort.dir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sort.dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, filter, excludeFilter, sort, filterKeys]);
};